import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FiGlobe, FiClock, FiUsers, FiUser, FiSend, FiPlus, FiMail, FiEye, FiCheck, FiTrash2 } from 'react-icons/fi';
import { toast } from '../../Toast';
import ShieldCheckIcon from '../../ShieldCheckIcon';
import { BackLink, HubHeader, IconButton, Pill, SelectBox, hubPath, useHubSite } from '../shared';
import { MESSAGES, MessageStatus, HistoryItem, STATUS_TONE, initials, brand } from './contactData';
import DeleteMessageModal from './DeleteMessageModal';

// small words stay lower case in the page title ("Request for Site Visit — Gold Mountain")
const SMALL = new Set(['a', 'an', 'and', 'as', 'at', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to', 'via']);
const titleCase = (s: string) => s.split(' ').map((w, i) => i > 0 && SMALL.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const SAVED_PLACEHOLDER = 'Insert a saved reply';
const SAVED: Record<string, (first: string) => string> = {
    'Thanks — we’ll be in touch': first => `Hi ${first},\n\nThank you for getting in touch. We've passed your message to the right person and they'll come back to you shortly.`,
    'Registry enquiries (Automic)': first => `Hi ${first},\n\nChanges to your holding details — address, bank account, dividend options — are made with our share registry, Automic, at investor.automic.com.au or on 1300 288 664.`,
    'Next investor webinar': first => `Hi ${first},\n\nOur next investor webinar is on Tuesday 8 September. You can register on the News & media page of our website, and the recording will be posted there afterwards.`,
};

const HISTORY_ICON: Record<HistoryItem['kind'], React.ReactNode> = { mail: <FiMail />, opened: <FiEye />, sent: <FiSend /> };

// Website Hub › Contact forms › one message (spec 408:10771)
const ContactMessage: React.FC<{ id: string }> = ({ id }) => {
    const history = useHistory();
    const { host, short } = useHubSite();
    const message = MESSAGES.find(m => m.id === id);
    const first = message ? message.name.split(' ')[0] : '';
    const signature = `Kind regards,\nWallace Turner\nManaging Director, ${short}`;
    const [status, setStatus] = useState<MessageStatus>(message?.status ?? 'New');
    const [log, setLog] = useState<HistoryItem[]>(message?.history ?? []);
    const [saved, setSaved] = useState(SAVED_PLACEHOLDER);
    const [reply, setReply] = useState(message ? brand(message.reply ?? `Hi ${first},\n\nThank you for your message.\n\n${signature}`, short, host) : '');
    const [deleting, setDeleting] = useState(false);

    const back = hubPath('contact-forms');
    if (!message) return <>
        <BackLink to={back} label="Back to Contact forms" />
        <p className="hub-empty">This message isn't in the preview sample.</p>
    </>;

    const change = (s: MessageStatus, note: string) => { setStatus(s); toast.info(`Preview only: ${note}, nothing was saved`); };
    const pickSaved = (v: string) => {
        setSaved(v);
        const make = SAVED[v];
        if (make) setReply(`${make(first)}\n\n${signature}`);
    };
    const send = () => {
        setStatus('Replied');
        setLog(xs => [...xs, { kind: 'sent', text: 'Reply sent by you', when: 'Just now' }]);
        toast.info(`Preview only: the reply to ${message.name} wasn't sent`);
    };

    const p = message.profile;
    const about: [string, React.ReactNode][] = p ? [
        ['Registered investor', <Pill key="r" tone="green">Yes · since {p.since}</Pill>],
        ['On share registry', p.registry],
        ['Subscribed to', p.subscribed],
        ['Location', p.location],
        ['Previous messages', p.previous],
        ['Questions asked', p.questions],
    ] : [['Registered investor', <Pill key="r" tone="grey">No sign‑up found</Pill>]];

    return <>
        <BackLink to={back} label="Back to Contact forms" />
        <HubHeader title={titleCase(message.subject)} sub={`From ${message.name} via the ${message.form} form · received ${message.receivedLong}`}>
            {status === 'New' && <button type="button" className="btn-outline with-icon" onClick={() => change('Replied', 'marked replied')}><FiCheck /> Mark Replied</button>}
            {status !== 'Archived' && <button type="button" className="btn-outline" onClick={() => change('Archived', 'archived')}>Archive</button>}
            {status !== 'Spam' && <button type="button" className="btn-outline with-icon" onClick={() => change('Spam', 'marked as spam')}><ShieldCheckIcon /> Mark as Spam</button>}
            <IconButton label="Delete" danger onClick={() => setDeleting(true)}><FiTrash2 /></IconButton>
        </HubHeader>

        <div className="cs-row">
            <div className="cs-main">
                <section className="card hub-msg">
                    <div className="hub-msg-from">
                        <span className="av">{initials(message.name)}</span>
                        <div className="t"><strong>{message.name}</strong><span>{message.email}{message.phone && ` · ${message.phone}`}</span></div>
                        <Pill tone={STATUS_TONE[status]}>{status}</Pill>
                    </div>
                    <div className="hub-msg-body">{brand(message.body, short, host)}</div>
                    <div className="hub-msg-meta">
                        <span><FiGlobe /> Sent from {message.page}</span>
                        <span><FiClock /> {message.sentAt}</span>
                        {message.matched && <span><FiUsers /> Matched to sign‑up · registered {message.matched}</span>}
                    </div>
                </section>

                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiSend /></span>
                        <div className="text"><h2>Reply by email</h2><div className="hint">Sent from info@{host} — {first} can reply straight back to you</div></div>
                        <SelectBox label="Saved replies" value={saved} options={[SAVED_PLACEHOLDER, ...Object.keys(SAVED)]} onChange={pickSaved} className="hub-saved-select" />
                    </div>
                    <div className="hub-composer">
                        <button type="button" className="link hub-ai" onClick={() => toast.info('Preview only: drafting with AI isn’t connected yet')}>✦ Draft with AI</button>
                        <textarea aria-label="Reply" value={reply} onChange={e => setReply(e.target.value)} />
                    </div>
                    <div className="hub-composer-foot">
                        <span><FiUsers /> Cc: megan.cole@{host}</span>
                        <button type="button" className="btn-outline" onClick={() => toast.info('Preview only: the draft wasn’t saved')}>Save Draft</button>
                        <button type="button" className="btn-primary with-icon" onClick={send} disabled={!reply.trim()}><FiSend /> Send Reply</button>
                    </div>
                </section>
            </div>

            <div className="cs-side">
                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiUser /></span>
                        <div className="text"><h2>About {first}</h2><div className="hint">What Investi knows</div></div>
                    </div>
                    <div className="hub-about">
                        {about.map(([k, v]) => <div key={k}><span>{k}</span><div>{v}</div></div>)}
                    </div>
                    <div className="hub-about-actions">
                        <button type="button" className="btn-outline with-icon" onClick={() => history.push('/share-registry')}><FiUsers /> Open in Share Registry</button>
                        <button type="button" className="btn-outline with-icon" onClick={() => toast.info('Preview only: sign‑up profiles aren’t built yet')}><FiUser /> View Sign‑Up Profile</button>
                        <button type="button" className="btn-outline with-icon" onClick={() => toast.info('Preview only: contact lists aren’t connected yet')}><FiPlus /> Add to a Contact List</button>
                    </div>
                </section>

                <section className="card">
                    <div className="section-title"><span className="icon-tile"><FiClock /></span><div className="text"><h2>Conversation history</h2></div></div>
                    <div className="hub-history">
                        {log.map((h, i) => <div key={i}><span className="hub-history-ic">{HISTORY_ICON[h.kind]}</span><div className="t"><strong>{h.text}</strong><span>{h.when}</span></div></div>)}
                    </div>
                </section>
            </div>
        </div>

        {deleting && <DeleteMessageModal name={message.name} subject={message.subject} onClose={() => setDeleting(false)}
            onArchive={() => { setDeleting(false); change('Archived', 'archived'); }}
            onDelete={() => { toast.info('Preview only: deleted here, nothing was saved'); history.push(back); }} />}
    </>;
};

export default ContactMessage;
